import {
  formatDualCurrencyDisplay,
  resolveUsdCeilFromIls,
} from "../formatDualCurrencyDisplay";

export const HOTELSRIVER_REHYDRATION_SNAPSHOT_KEY =
  "HOTELSRIVER_PACKAGE_REHYDRATION_PRICING_SNAPSHOT";

function normalizeCurrencyCode(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return null;
  if (raw === "₪" || raw === "NIS") return "ILS";
  if (raw === "US$") return "USD";
  return raw;
}

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveDynamicPackageOrderCurrency(order) {
  const explicitCandidates = [
    order?.ourPrice?.sourceCurrency,
    order?.ourPrice?.currency,
    order?.currency,
    order?.selectedHotelPackage?.CurrencyCode,
    order?.selectedHotel?.currency,
    order?.originalCurrency,
    order?.selectedHotelPackage?.ourPrice?.originalCurrency,
    order?.selectedHotelPackage?.ourPrice?.currency,
  ];
  for (const candidate of explicitCandidates) {
    const normalized = normalizeCurrencyCode(candidate);
    if (normalized) return normalized;
  }
  return "USD";
}

export function normalizeDynamicPackageMoneyToIls(
  amount,
  sourceCurrency,
  usdToIls
) {
  const raw = toPositiveNumber(amount);
  if (raw == null) return null;
  const source = normalizeCurrencyCode(sourceCurrency) || "USD";
  if (source === "ILS") return raw;
  if (source === "USD" && typeof usdToIls === "function") {
    const converted = Number(usdToIls(raw));
    return Number.isFinite(converted) && converted > 0 ? converted : null;
  }
  return raw;
}

function resolvePricingIdentifiers(currentHotelOrder) {
  return {
    packageId:
      currentHotelOrder?.selectedHotelPackage?.packageID ??
      currentHotelOrder?.selectedHotelPackage?.PackageId ??
      currentHotelOrder?.selectedHotelPackage?.packageId ??
      null,
    dealId:
      currentHotelOrder?.selectedHotelsRiverPackageOption?.dealId ??
      currentHotelOrder?.selectedHotelsRiverPackageOption?.ldsDealId ??
      null,
  };
}

/**
 * HotelsRiver sidebar total (contract): selected card finalPackagePrice (ILS)
 * + handling fee as entered (fixed ILS — uses {@link fixedCommissionInputIls} when present)
 * + credit card fee only when {@link getCreditCardFeePercent} returns &gt; 0.
 *
 * Does not use {@link currentHotelOrder.ourPrice} headline totals as the package base.
 */
export function buildHotelsRiverResolvedPricingVm({
  currentHotelOrder = null,
  profileSelectedHotelsRiverPackageOption = null,
  usdToIls,
  getCreditCardFeePercent,
}) {
  const { packageId, dealId } = resolvePricingIdentifiers(currentHotelOrder);
  const orderOurPrice = currentHotelOrder?.ourPrice || null;
  const orderOption = currentHotelOrder?.selectedHotelsRiverPackageOption || null;
  const profileOption = profileSelectedHotelsRiverPackageOption || null;

  const selectedCardFinalPackagePriceIls = toPositiveNumber(
    profileOption?.finalPackagePrice ?? orderOption?.finalPackagePrice
  );

  let sourceOfBasePrice = "none";
  if (selectedCardFinalPackagePriceIls != null) {
    if (profileOption?.finalPackagePrice != null) {
      sourceOfBasePrice =
        "profileSelectedHotelsRiverPackageOption.finalPackagePrice";
    } else {
      sourceOfBasePrice =
        "currentHotelOrder.selectedHotelsRiverPackageOption.finalPackagePrice";
    }
  }

  const handleFeeData = orderOurPrice?.handleFeeData || null;
  const hfType = handleFeeData?.type;
  const hfVal = handleFeeData?.value;

  let handlingFeeInputIls = 0;
  let sourceOfHandlingFee = "none";

  if (
    hfType === "%" &&
    hfVal != null &&
    String(hfVal).trim() !== "" &&
    selectedCardFinalPackagePriceIls != null
  ) {
    const pct = Number(hfVal);
    if (Number.isFinite(pct)) {
      handlingFeeInputIls = Math.round((selectedCardFinalPackagePriceIls * pct) / 100);
      sourceOfHandlingFee = "handleFeeData.percent_of_card_base";
    }
  } else if (hfType === "+") {
    const fixedIls = orderOurPrice?.fixedCommissionInputIls;
    if (fixedIls != null && Number.isFinite(Number(fixedIls))) {
      handlingFeeInputIls = Math.round(Number(fixedIls));
      sourceOfHandlingFee = "ourPrice.fixedCommissionInputIls";
    } else if (toPositiveNumber(orderOurPrice?.handlingFee) != null) {
      const orderCurrency = resolveDynamicPackageOrderCurrency(currentHotelOrder);
      const conv = normalizeDynamicPackageMoneyToIls(
        orderOurPrice.handlingFee,
        orderCurrency,
        usdToIls
      );
      handlingFeeInputIls = conv != null ? Math.round(conv) : 0;
      sourceOfHandlingFee = "ourPrice.handlingFee_legacy_converted_once";
    }
  }

  const ccPctRaw =
    typeof getCreditCardFeePercent === "function"
      ? Number(getCreditCardFeePercent())
      : 0;
  const ccPct =
    Number.isFinite(ccPctRaw) && ccPctRaw > 0 ? ccPctRaw : 0;

  let creditCardFeeIls = 0;
  let subtotalBeforeCcIls = null;
  if (selectedCardFinalPackagePriceIls != null) {
    subtotalBeforeCcIls =
      selectedCardFinalPackagePriceIls + handlingFeeInputIls;
    if (ccPct > 0) {
      creditCardFeeIls = Math.round((subtotalBeforeCcIls * ccPct) / 100);
    }
  }

  let finalTotalIls = null;
  if (selectedCardFinalPackagePriceIls != null) {
    finalTotalIls = Math.round(
      selectedCardFinalPackagePriceIls + handlingFeeInputIls + creditCardFeeIls
    );
  }

  const priceBreakdown =
    profileOption?.priceBreakdown ||
    orderOption?.priceBreakdown ||
    null;
  const hotelTotalRaw = toPositiveNumber(priceBreakdown?.rawHotelPrice);
  const flightTotalRaw = toPositiveNumber(priceBreakdown?.rawFlightPrice);

  const missingFields = [];
  if (finalTotalIls == null) missingFields.push("finalTotalIls");
  if (!orderOption && !profileOption)
    missingFields.push("selectedHotelsRiverPackageOption");
  if (flightTotalRaw == null) missingFields.push("priceBreakdown.rawFlightPrice");
  if (hotelTotalRaw == null) missingFields.push("priceBreakdown.rawHotelPrice");

  const expectedTotalIls =
    selectedCardFinalPackagePriceIls != null
      ? Math.round(
          selectedCardFinalPackagePriceIls +
            handlingFeeInputIls +
            creditCardFeeIls
        )
      : null;

  const pricingInput = {
    selectedCardFinalPackagePriceIls,
    handlingFeeInputIls,
    creditCardFeeIls,
    creditCardFeePercent: ccPct > 0 ? ccPct : null,
    expectedTotalIls,
    subtotalBeforeCcIls,
    sourceOfBasePrice,
    sourceOfHandlingFee,
  };

  return {
    sourceCurrency: "ILS",
    targetCurrency: "ILS",
    hotelTotalRaw,
    flightTotalRaw,
    handlingFeeRaw: handlingFeeInputIls,
    creditCardFeeRaw: creditCardFeeIls,
    finalTotalRaw: finalTotalIls,
    finalTotalIls,
    finalTotalIlsBeforeFee:
      subtotalBeforeCcIls != null ? subtotalBeforeCcIls : finalTotalIls,
    finalTotalIlsAfterFee: finalTotalIls,
    sourceOfPricing: "hotelsRiver.simple_card_plus_handling_plus_cc",
    missingFields,
    fallbackUsed: false,
    usedStaleOrderVersusCardOverride: false,
    packageId,
    dealId,
    pricingInput,
    /** DEV assertion payload — recomputed total must equal {@link finalTotalIls}. */
    simpleAssert: pricingInput,
  };
}

/**
 * Profile sidebar total: ILS primary with USD in parentheses.
 *
 * @param {object|null|undefined} vm — output of {@link buildHotelsRiverResolvedPricingVm}
 * @param {{ ilsToUsd?: (n: number, fixed?: boolean) => number, currencyRate?: { USD?: number } | null }} deps
 */
export function buildHotelsRiverPackageTotalDualCurrencyDisplay(vm, deps = {}) {
  if (!vm || typeof vm !== "object") return null;
  const ils = vm.finalTotalIls;
  const ilsRounded =
    ils != null && Number.isFinite(Number(ils)) && Number(ils) > 0
      ? Math.round(Number(ils))
      : null;
  if (ilsRounded == null) return null;

  const { ilsToUsd, currencyRate } = deps;
  const usdForParen = resolveUsdCeilFromIls(ilsRounded, ilsToUsd, currencyRate);

  return formatDualCurrencyDisplay({
    usd: usdForParen,
    ils: ilsRounded,
    primaryCurrency: "ILS",
  });
}
